import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { CoverInput } from '@/entities/appearance';
import { attachmentPreviewUrl, useAttachments, useAttachmentUploader } from '@/entities/attachment';
import { CardCover, useClearCardCover, useSetCardCover } from '@/entities/board';
import type { CardDetail } from '@/entities/card';
import { FocalPoint, GradientEditor } from '@/features/appearance-editor';
import { FormError } from '@/shared/forms';
import { Button, ColorSwatches, Dialog } from '@/shared/ui';

import styles from './CoverEditor.module.css';

type Kind = 'none' | 'color' | 'gradient' | 'image';
const KINDS: readonly Kind[] = ['none', 'color', 'gradient', 'image'];

interface Props {
  card: CardDetail;
  boardId: string;
  onClose: () => void;
}

const DEFAULT_GRADIENT = { start: '#0079bf', end: '#5ba4cf', angle: 135 };

/**
 * Cover editor: none, a color, a gradient or one of the card's images (or a new one, which is
 * attached to the card first: one stored file serves as attachment and cover). Save/Cancel with
 * a live preview; "No cover" removes only the cover, never an attachment.
 */
export function CoverEditor({ card, boardId, onClose }: Props) {
  const { t } = useTranslation();
  const attachments = useAttachments(card.id);
  const uploader = useAttachmentUploader(card.id, boardId);
  const setCover = useSetCardCover();
  const clearCover = useClearCardCover();
  const current = card.cover;
  const [mode, setMode] = useState<Kind>(current?.kind ?? 'none');
  const [chosen, setDraft] = useState<CoverInput | null>(current);
  const [error, setError] = useState<unknown>(null);
  const images = (attachments.data ?? []).filter((a) => a.previewable);
  const uploading = uploader.items.some((item) => item.status === 'uploading');
  const newest = images[images.length - 1];
  // In image mode without a pick yet (or right after uploading), the newest image is the pick.
  const draft: CoverInput | null =
    mode === 'image' && chosen?.kind !== 'image' && newest
      ? { kind: 'image', attachment_id: newest.id, position_x: 0.5, position_y: 0.5 }
      : mode === 'none'
        ? null
        : chosen;
  const kind: Kind = mode;

  const select = (next: Kind) => {
    setMode(next);
    switch (next) {
      case 'none':
        setDraft(null);
        return;
      case 'color':
        setDraft({ kind: 'color', color: draft?.kind === 'color' ? draft.color : '#0079bf' });
        return;
      case 'gradient':
        setDraft(draft?.kind === 'gradient' ? draft : { kind: 'gradient', ...DEFAULT_GRADIENT });
        return;
      case 'image':
        // Without any image yet the file picker below does the work.
        setDraft(chosen?.kind === 'image' ? chosen : null);
        return;
    }
  };

  const save = () => {
    setError(null);
    const done = { onSuccess: onClose, onError: setError };
    if (draft === null) clearCover.mutate(card.id, done);
    else setCover.mutate({ cardId: card.id, cover: draft }, done);
  };

  return (
    <Dialog title={t('cover.title')} onClose={onClose}>
      <form
        className={styles.form}
        onSubmit={(event) => {
          event.preventDefault();
          save();
        }}
      >
        <div className={styles.preview} data-testid="cover-preview">
          {draft ? (
            <CardCover cover={draft} className={styles.previewCover} testId="cover-preview-cover" />
          ) : (
            <span className={styles.none}>{t('cover.none')}</span>
          )}
        </div>

        <div className={styles.kinds} role="radiogroup" aria-label={t('cover.kind')}>
          {KINDS.map((value) => (
            <button
              key={value}
              type="button"
              role="radio"
              aria-checked={kind === value}
              className={styles.kind}
              data-testid={`cover-kind-${value}`}
              onClick={() => {
                select(value);
              }}
            >
              {t(`cover.kinds.${value}`)}
            </button>
          ))}
        </div>

        {draft?.kind === 'color' && (
          <ColorSwatches
            label={t('cover.color')}
            value={draft.color}
            onChange={(color) => {
              if (color) setDraft({ kind: 'color', color });
            }}
          />
        )}
        {draft?.kind === 'gradient' && (
          <GradientEditor
            value={{ start: draft.start, end: draft.end, angle: draft.angle }}
            onChange={(gradient) => {
              setDraft({ kind: 'gradient', ...gradient });
            }}
          />
        )}
        {kind === 'image' && (
          <div className={styles.images}>
            {images.length > 0 && (
              <ul className={styles.thumbs} aria-label={t('cover.chooseImage')}>
                {images.map((image) => (
                  <li key={image.id}>
                    <button
                      type="button"
                      className={styles.thumb}
                      aria-pressed={draft?.kind === 'image' && draft.attachment_id === image.id}
                      aria-label={image.filename}
                      onClick={() => {
                        setDraft({
                          kind: 'image',
                          attachment_id: image.id,
                          position_x: draft?.kind === 'image' ? draft.position_x : 0.5,
                          position_y: draft?.kind === 'image' ? draft.position_y : 0.5,
                        });
                      }}
                    >
                      <img src={attachmentPreviewUrl(image.id, 'thumb')} alt="" loading="lazy" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
            <label className={styles.upload}>
              <span>{t('cover.uploadImage')}</span>
              <input
                type="file"
                accept="image/jpeg,image/png,image/gif,image/webp"
                data-testid="cover-file"
                disabled={uploading}
                onChange={(event) => {
                  uploader.add([...(event.target.files ?? [])]);
                  event.target.value = '';
                }}
              />
            </label>
            {uploader.items.map((item) => (
              <p key={item.key} className={styles.hint} role="status">
                {item.name}:{' '}
                {item.status === 'uploading' ? `${Math.round(item.progress * 100)}%` : item.status}
              </p>
            ))}
            {images.length === 0 && uploader.items.length === 0 && (
              <p className={styles.hint}>{t('cover.noImages')}</p>
            )}
            {draft?.kind === 'image' && (
              <FocalPoint
                src={attachmentPreviewUrl(draft.attachment_id, 'medium')}
                x={draft.position_x}
                y={draft.position_y}
                onChange={(x, y) => {
                  setDraft({ ...draft, position_x: x, position_y: y });
                }}
              />
            )}
          </div>
        )}

        <FormError error={error} />
        <div className={styles.actions}>
          <Button
            type="submit"
            variant="primary"
            disabled={
              setCover.isPending ||
              clearCover.isPending ||
              (kind === 'image' && draft?.kind !== 'image')
            }
          >
            {t('common.save')}
          </Button>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
        </div>
      </form>
    </Dialog>
  );
}
